import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type PhotoLightboxProps = {
  photos: string[];
  initialIndex: number;
  alt: string;
  onClose: () => void;
};

function PhotoLightbox({ photos, initialIndex, alt, onClose }: PhotoLightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  const navigate = useCallback(
    (dir: 1 | -1) => setIndex((i) => (i + dir + photos.length) % photos.length),
    [photos.length]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        navigate(1);
      } else if (e.key === 'ArrowLeft') {
        navigate(-1);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, onClose]);

  const multiPhoto = photos.length > 1;

  return createPortal(
    <div
      className='map-poi-lightbox'
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      aria-label='Photo viewer'
    >
      <div
        className='map-poi-lightbox__content'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className='map-poi-lightbox__close'
          onClick={onClose}
          type='button'
          aria-label='Close'
        >
          <CloseIcon />
        </button>

        {multiPhoto && (
          <button
            className='map-poi-lightbox__nav map-poi-lightbox__nav--prev'
            onClick={() => navigate(-1)}
            type='button'
            aria-label='Previous photo'
          >
            <ArrowBackIosNewIcon />
          </button>
        )}

        <img
          className='map-poi-lightbox__img'
          src={photos[index]}
          alt={`${alt} ${index + 1}`}
        />

        {multiPhoto && (
          <button
            className='map-poi-lightbox__nav map-poi-lightbox__nav--next'
            onClick={() => navigate(1)}
            type='button'
            aria-label='Next photo'
          >
            <ArrowForwardIosIcon />
          </button>
        )}

        {multiPhoto && (
          <div className='map-poi-lightbox__dots'>
            {photos.map((_, i) => (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: index is the correct identity for photo dots
                key={i}
                type='button'
                className={`map-poi-lightbox__dot${i === index ? ' map-poi-lightbox__dot--active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to photo ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

PhotoLightbox.displayName = 'PhotoLightbox';
export default PhotoLightbox;
